import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Save, FileDown, SlidersHorizontal, RefreshCw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/negotiationLabels";
import { draftToData, selectedStatFields } from "@/lib/negotiationDraft";
import { ReadinessBar, StepNav, StepFooter, PreviewPanel } from "@/components/negotiation/workspace/WorkspaceChrome";
import { FinalReviewModal } from "@/components/negotiation/workspace/FinalReviewModal";
import { IdentityStep, StatsStep } from "@/components/negotiation/workspace/StepIdentityStats";
import { SportProfileStep } from "@/components/negotiation/workspace/StepSportProfile";
import { ContractStep, CareerStep } from "@/components/negotiation/workspace/StepContractCareer";
import { ContactStep } from "@/components/negotiation/workspace/StepContact";
import { translateFr as tfr } from "@/i18n";

const setPath = (obj, path, value) => {
  if (path.length === 1) return { ...obj, [path[0]]: value };
  return { ...obj, [path[0]]: setPath(obj?.[path[0]] || {}, path.slice(1), value) };
};

export default function NegotiationWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: false });
  const [draft, setDraft] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [step, setStep] = useState(1);
  const [saveState, setSaveState] = useState("idle");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  const dirtyRef = useRef(false);
  const saveTimer = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;

  const load = useCallback(async () => {
    setState({ loading: true, error: false });
    try {
      const r = await api.get(`/players/${id}/negotiation-profile/draft`);
      setState({ loading: false, error: false, player: r.data.player, config: r.data.config });
      setDraft(r.data.draft);
      setReadiness(r.data.readiness);
      setStep(r.data.draft.current_step || 1);
    } catch (e) {
      if (e?.response?.status === 404) { navigate("/pipeline"); return; }
      setState({ loading: false, error: true });
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async () => {
    if (!draftRef.current) return;
    setSaveState("saving");
    try {
      const r = await api.patch(`/players/${id}/negotiation-profile/draft`, { draft: draftRef.current });
      setReadiness(r.data.readiness);
      setSaveState("saved");
      dirtyRef.current = false;
    } catch (e) {
      if (e?.response?.status !== 402) setSaveState("error");
      else setSaveState("idle");
    }
  }, [id]);

  const update = useCallback((path, value) => {
    setDraft((d) => setPath(d, path, value));
    dirtyRef.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 1200);
  }, [persist]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const goStep = (n) => {
    setStep(n);
    update(["current_step"], n);
  };

  // Debounced preview data (300 ms) — inputs update immediately, preview lags slightly.
  const [previewDraft, setPreviewDraft] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => setPreviewDraft(draft), 300);
    return () => clearTimeout(timer);
  }, [draft]);
  const previewData = useMemo(
    () => draftToData(previewDraft || draft, state.config),
    [previewDraft, draft, state.config]
  );
  const statFields = useMemo(
    () => selectedStatFields(previewDraft || draft, state.config),
    [previewDraft, draft, state.config]
  );

  const generate = async ({ audience, template_id }) => {
    setGenerating(true);
    try {
      clearTimeout(saveTimer.current);
      await persist();
      const lang = localStorage.getItem("athlynx_lang") === "en" ? "en" : "fr";
      const r = await api.post(`/players/${id}/negotiation-profile/draft/generate`, { audience, template_id, lang });
      const dl = await api.get(`/players/${id}/documents/${r.data.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([dl.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${tfr("PDF généré")} (V${r.data.version}) ${tfr("et ajouté aux Documents")}`);
      setReviewOpen(false);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || tfr("Impossible de générer le PDF"));
    } finally { setGenerating(false); }
  };

  if (state.loading) {
    return (
      <div className="px-4 md:px-6 py-6 grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)] gap-4" data-testid="ws-loading">
        <Skeleton className="h-[70vh] rounded-2xl" />
        <Skeleton className="h-[70vh] rounded-2xl hidden xl:block" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="py-16 text-center" data-testid="ws-error">
        <p className="text-sm text-muted-foreground">{t("negotiationProfile.error.load")}</p>
        <Button variant="outline" onClick={load} className="mt-4 gap-2" data-testid="ws-retry">
          <RefreshCw className="h-4 w-4" /> {t("negotiationProfile.error.retry")}
        </Button>
      </div>
    );
  }
  if (!draft) return null;
  const { player, config } = state;
  const steps = config.steps;
  const stepProps = { draft, update, player, config, playerId: id };

  return (
    <div className="px-4 md:px-6 py-4 pb-6" data-testid="negotiation-workspace">
      {/* Header */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3" aria-label="Fil d'Ariane">
        <button onClick={() => navigate(`/players/${id}`)}
          className="rounded-full p-1.5 hover:bg-secondary transition-colors -ml-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
          aria-label={tfr("Retour au profil de l'athlète")} data-testid="ws-back">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <Link to="/pipeline" className="hover:text-foreground transition-colors">{t("negotiationProfile.breadcrumb.players")}</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/players/${id}`} className="hover:text-foreground transition-colors">{player.name}</Link>
        <span aria-hidden="true">/</span>
        <span className="text-foreground font-medium">{t("negotiationProfile.breadcrumb.sheet")}</span>
      </nav>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight" data-testid="ws-title">{t("negotiationProfile.title")}</h1>
            <Badge variant="outline" className="text-[10px] bg-secondary text-muted-foreground border-border" data-testid="ws-status-badge">
              {t("negotiationProfile.status.draft")}
            </Badge>
            <span className="text-[11px] text-muted-foreground" aria-live="polite" data-testid="ws-save-state">
              {saveState === "saving" ? t("negotiationProfile.save.saving")
                : saveState === "saved" ? t("negotiationProfile.save.saved")
                  : saveState === "error" ? t("negotiationProfile.save.error") : ""}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{t("negotiationProfile.subtitle")}</p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-1.5"
            onClick={() => { clearTimeout(saveTimer.current); persist(); }} data-testid="ws-save-btn">
            <Save className="h-3.5 w-3.5" /> {t("negotiationProfile.actions.saveDraft")}
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5 xl:hidden"
            onClick={() => setMobilePreview(true)} data-testid="ws-mobile-preview-btn">
            <Eye className="h-3.5 w-3.5" /> {t("negotiationProfile.preview.mobileOpen")}
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5"
            onClick={() => navigate(`/players/${id}/negotiation-profile/import`)}
            title={t("negotiationProfile.advanced.warning")} data-testid="ws-advanced-btn">
            <SlidersHorizontal className="h-3.5 w-3.5" /> {t("negotiationProfile.actions.advancedEdit")}
          </Button>
          <Button size="sm" className="h-9 gap-1.5 bg-athlynx hover:bg-athlynx/90 text-white"
            onClick={() => setReviewOpen(true)} data-testid="ws-generate-btn">
            <FileDown className="h-3.5 w-3.5" /> {t("negotiationProfile.actions.generatePdf")}
          </Button>
        </div>
      </div>

      {/* Workspace grid */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)] gap-4 items-start">
        {/* Editor panel */}
        <div className="flex flex-col min-w-0 rounded-2xl border border-border bg-card overflow-hidden xl:h-[calc(100dvh-215px)] xl:min-h-[560px]"
          data-testid="ws-editor-panel">
          <ReadinessBar readiness={readiness} />
          <StepNav steps={steps} current={step} readiness={readiness} onSelect={goStep} />
          <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-5">
            {step === 1 && <IdentityStep {...stepProps} />}
            {step === 2 && <StatsStep {...stepProps} />}
            {step === 3 && <SportProfileStep {...stepProps} />}
            {step === 4 && <ContractStep {...stepProps} />}
            {step === 5 && <CareerStep {...stepProps} />}
            {step === 6 && <ContactStep {...stepProps} />}
          </div>
          <StepFooter step={step} total={steps.length}
            onPrev={() => goStep(Math.max(1, step - 1))}
            onNext={() => goStep(Math.min(steps.length, step + 1))} />
        </div>

        {/* Preview panel */}
        <div className="xl:sticky xl:top-4 xl:h-[calc(100dvh-215px)] xl:min-h-[560px] min-w-0 flex">
          <PreviewPanel
            player={player}
            sportLabel={config.sport_label}
            data={previewData}
            statFields={statFields}
            agent={{ name: user?.name, email: user?.email }}
            mobileOpen={mobilePreview}
            onMobileClose={() => setMobilePreview(false)}
          />
        </div>
      </div>

      <FinalReviewModal open={reviewOpen} onOpenChange={setReviewOpen} readiness={readiness}
        playerName={player.name} onGenerate={generate} generating={generating} />
    </div>
  );
}
